import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

type Status = 'ok' | 'failed';
type ConnectionStatus = 'connected' | 'disconnected';

interface ServerStatus {
  status: Status;
  timestamp: string;
  database: {
    status: ConnectionStatus;
  }
  storage: {
    status: ConnectionStatus;
  }
  process: {
    uptime: string;
    responseTime: string;
    memory: {
      rss: string;
      heapTotal: string;
      heapUsed: string;
    }
  }
  system: {
    hostname: string;
    arch: string;
    memory: {
      total: string;
      free: string;
    }
    cpu: {
      loadAverage: number[];
      cores: number;
    }
    os: {
      platform: NodeJS.Platform;
      release: string;
      uptime: string;
    }
  }
}

export const allowedAuthMethods = ['username_password', 'api_key', 'public'] as const;
export type AllowedMethods = typeof allowedAuthMethods[number];

interface Config {
  auth: {
    allowedMethods: AllowedMethods[];
    sessionExpiresInSeconds: number;
  };
  api: {
    version: number;
    versionString: string;
  };
}

export const serverApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Not `/health`: that one is the public liveness probe the container calls, and it
    // answers with status and timestamp only. The detailed report requires `health:get`.
    getStatus: builder.query<ServerStatus, void>({
      query: () => '/health/details',
      onQueryStarted: handleApiResult(),
    }),
    getConfig: builder.query<Config, void>({
      query: () => '/config',
      providesTags: ['Config'],
      onQueryStarted: handleApiResult(),
    }),
    updateConfig: builder.mutation<void, Omit<Config, 'api'>>({
      query: (config) => ({ url: '/config', method: 'PUT', body: config }),
      invalidatesTags: ['Config'],
      onQueryStarted: handleApiResult('Api_ServerConfig_Update_Successful'),
    }),
  }),
});

export const { useGetStatusQuery, useGetConfigQuery, useUpdateConfigMutation } = serverApiSlice;
